import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { getTableConfig, clearTableConfig } from '#action/table/configurable-table';
import { tableMap } from './table-body-cell';
import { tableHeadMap } from './table-head-cell';
import ColumnCustomize from './column-customize';
import { StyledTable, StyledTableRow } from './styles';

const ConfigurableTable = ({
  data = [],
  headCellProps,
  bodyCellProps,
  tableName
}) => {
  const dispatch = useDispatch();
  const [init, setInit] = useState(false);
  const { onClick, onMouseDown, loading, withoutHover = false } = bodyCellProps;
  const columns = useSelector(s => s.configurableTable.columns, shallowEqual);
  const renderedColumns = columns.filter(col => col.isVisible);
  const tableBodyColMap = tableMap[tableName];
  const tableHeadColMap = tableHeadMap[tableName];

  const withDisabledColumns = useMemo(() => columns.map(c => ({
    ...c,
    disabled: c.id === headCellProps.orderBy || c.root
  })), [columns, headCellProps.orderBy]);

  useEffect(() => {
    const fetchTableColumns = async () => {
      await dispatch(getTableConfig(tableName));
      setInit(true);
    };

    fetchTableColumns();

    return () => {
      dispatch(clearTableConfig());
    };
  }, [dispatch, tableName]);

  const getHeadCell = useCallback(rowCellId => {
    const Cell = tableHeadColMap[rowCellId] ?? null;
    if (Cell) {
      return <Cell key={rowCellId} {...headCellProps} />;
    }
    return null;
  }, [tableHeadColMap, headCellProps]);

  const getBodyCell = useCallback((rowCellId, cellData) => {
    const Cell = tableBodyColMap[rowCellId] ?? null;
    if (Cell) {
      return <Cell key={rowCellId} {...bodyCellProps} data={cellData} />;
    }
    return null;
  }, [tableBodyColMap, bodyCellProps]);

  const handleRowClick = useCallback(d => onClick?.(d), [onClick]);

  const handleRowMouseDown = useCallback(d => onMouseDown?.(d), [onMouseDown]);

  return (
    <StyledTable loading={loading}>
      <TableHead>
        <TableRow>
          {renderedColumns.map(col => getHeadCell(col.id))}
          <ColumnCustomize
            tableName={tableName}
            columns={withDisabledColumns}
            order={headCellProps.order}
          />
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map(d => (
          <StyledTableRow
            key={d.id}
            hover={!loading}
            clickable={!withoutHover && !loading}
            skeleton={!init}
            onClick={handleRowClick(d)}
            onMouseDown={handleRowMouseDown(d)}
          >
            {renderedColumns.map(col => getBodyCell(col.id, d))}
            <TableCell align="left" width={20} />
          </StyledTableRow>
        ))}
      </TableBody>
    </StyledTable>
  );
};

export default ConfigurableTable;
